import { useState, useEffect } from 'react';
import { ScrollView, StyleSheet, Platform, Dimensions } from 'react-native';
import { Switch, Button, TextInput, Appbar, useTheme, Surface, Text, Snackbar, List, Portal, Dialog } from 'react-native-paper';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import * as Location from 'expo-location';
import {
  updateNotificationPreferences,
  getUserDetails,
  updateDrivingLimitMode,
  DEFAULT_DRIVING_LIMIT_MODE,
  type DrivingLimitMode,
} from '../../src/api/apiService';
import { DrivingLimitModeSetting } from '../../src/features/driver/workTime/DrivingLimitModeSetting';
import { modeSaved, resyncDrivingLimitAfterModeChange } from '../../src/lib/drivingLimitMode';
import { useRouter } from 'expo-router';
import { track } from '../../src/lib/analytics';
import { useTranslation } from 'react-i18next';
import { MaterialIcons } from '@expo/vector-icons';

type PermissionStatus = 'granted' | 'denied' | 'undetermined';

export default function Settings() {
  const { width: SCREEN_WIDTH } = Dimensions.get('window');
  const isLargeScreen = SCREEN_WIDTH > 1024;
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  const [stopTimeThreshold, setStopTimeThreshold] = useState('5');
  const [distanceThreshold, setDistanceThreshold] = useState('100');
  const [drivingLimitMode, setDrivingLimitMode] = useState<DrivingLimitMode>(DEFAULT_DRIVING_LIMIT_MODE);
  const [savingDrivingLimitMode, setSavingDrivingLimitMode] = useState(false);
  const [permissions, setPermissions] = useState({
    notifications: false,
    foregroundLocation: false,
    backgroundLocation: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const theme = useTheme();
  const router = useRouter();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const loadPreferencesAndPermissions = async () => {
      try {
        const response = await getUserDetails();
        if (response.ok && response.data.profile) {
          const { notification_enabled, stop_time_threshold, distance_threshold } = response.data.profile;
          setNotificationEnabled(notification_enabled ?? false);
          setStopTimeThreshold((stop_time_threshold ?? 5).toString());
          setDistanceThreshold((distance_threshold ?? 100).toString());
          setDrivingLimitMode(response.data.profile.driving_limit_mode ?? DEFAULT_DRIVING_LIMIT_MODE);
          await AsyncStorage.setItem('notificationEnabled', (notification_enabled ?? false).toString());
          await AsyncStorage.setItem('stopTimeThreshold', (stop_time_threshold ?? 5).toString());
          await AsyncStorage.setItem('distanceThreshold', (distance_threshold ?? 100).toString());
        } else {
          throw new Error('Resposta inválida do backend');
        }
      } catch (error) {
        console.error('Erro ao carregar preferências:', error);
        const enabled = await AsyncStorage.getItem('notificationEnabled');
        const threshold = await AsyncStorage.getItem('stopTimeThreshold');
        const distance = await AsyncStorage.getItem('distanceThreshold');
        setNotificationEnabled(enabled === 'true');
        setStopTimeThreshold(threshold || '5');
        setDistanceThreshold(distance || '100');
      }

      if (Platform.OS === 'web') {
        setPermissions({
          notifications: false,
          foregroundLocation: false,
          backgroundLocation: false,
        });
      } else {
        const { status: notificationStatus } = await Notifications.getPermissionsAsync();
        const { status: foregroundStatus } = await Location.getForegroundPermissionsAsync();
        const { status: backgroundStatus } = await Location.getBackgroundPermissionsAsync();
        setPermissions({
          notifications: notificationStatus === 'granted',
          foregroundLocation: foregroundStatus === 'granted',
          backgroundLocation: backgroundStatus === 'granted',
        });
      }
      setIsLoading(false);
    };
    loadPreferencesAndPermissions();
  }, []);

  const savePreferences = async () => {
    setFieldErrors({}); // Clear previous errors
    try {
      const response = await updateNotificationPreferences(
        notificationEnabled, 
        parseInt(stopTimeThreshold),
        parseInt(distanceThreshold)
      );
      
      if (response.ok) {
        await AsyncStorage.setItem('notificationEnabled', notificationEnabled.toString());
        await AsyncStorage.setItem('stopTimeThreshold', stopTimeThreshold);
        await AsyncStorage.setItem('distanceThreshold', distanceThreshold);
        track('driver_notification_preferences_saved', {
          notification_enabled: notificationEnabled,
          stop_time_threshold_minutes: parseInt(stopTimeThreshold),
          distance_threshold_meters: parseInt(distanceThreshold),
        });
        setSnackbarMessage(t('preferencesSaved'));
        setSnackbarVisible(true);
      } else {
        // Check for field errors
        if (response.data && typeof response.data === 'object') {
          const errors: { [key: string]: string[] } = response.data;
          const formatted: { [key: string]: string } = {};
          if (errors.stop_time_threshold) {
            formatted.stopTimeThreshold = errors.stop_time_threshold[0];
          }
          if (errors.distance_threshold) {
            formatted.distanceThreshold = errors.distance_threshold[0];
          }
          setFieldErrors(formatted);
          setSnackbarMessage(t('errorSavingPreferences'));
          setSnackbarVisible(true);
          return;
        }
        throw new Error('Failed to save preferences');
      }
    } catch (error) {
      console.error('Erro ao salvar preferências:', error);
      setSnackbarMessage(t('errorSavingPreferences'));
      setSnackbarVisible(true);
    }
  };

  // ALL-174: saved on tap, server-side, so the choice follows the driver.
  const changeDrivingLimitMode = async (mode: DrivingLimitMode) => {
    const previous = drivingLimitMode;
    const label = (m: DrivingLimitMode) =>
      t(m === 'calendar_day' ? 'workTime.mode.calendarDay.title' : 'workTime.mode.rolling.title');
    setDrivingLimitMode(mode);
    setSavingDrivingLimitMode(true);
    try {
      const response = await updateDrivingLimitMode(mode);
      if (!modeSaved(response, mode)) throw new Error('Failed to save driving limit mode');
      track('driver_driving_limit_mode_saved', { mode });
      void resyncDrivingLimitAfterModeChange(t, i18n?.language);
      setSnackbarMessage(t('workTime.mode.saved', { mode: label(mode) }));
    } catch (error) {
      console.error('Erro ao guardar contagem do limite:', error);
      setDrivingLimitMode(previous);
      setSnackbarMessage(t('workTime.mode.saveError', { mode: label(previous) }));
    } finally {
      setSavingDrivingLimitMode(false);
      setSnackbarVisible(true);
    }
  };

  const showPermissionDialog = (title: string, message: string) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setShowDialog(true);
  };

  const requestPermission = async (type: 'notifications' | 'foreground' | 'background') => {
    let status: PermissionStatus;
    try {
      if (Platform.OS === 'web') {
        showPermissionDialog(
          t('permissionDenied'),
          t('permissionExplanation', { type: t(type) })
        );
        return;
      }

      if (type === 'notifications') {
        const { status: newStatus } = await Notifications.requestPermissionsAsync();
        status = newStatus;
      } else if (type === 'foreground') {
        const { status: newStatus } = await Location.requestForegroundPermissionsAsync();
        status = newStatus;
      } else {
        const { status: newStatus } = await Location.requestBackgroundPermissionsAsync();
        status = newStatus;
      }

      setPermissions((prev) => {
        const updated = {
          ...prev,
          [type === 'notifications' ? 'notifications' : type === 'foreground' ? 'foregroundLocation' : 'backgroundLocation']: status === 'granted',
        };

        if (status !== 'granted') {
          showPermissionDialog(
            t('permissionDenied'),
            t('permissionExplanation', { type: t(type) })
          );
        }
        return updated;
      });
    } catch (error) {
      console.error(`Erro ao solicitar permissão ${type}:`, error);
    }
  };

  const handleStopTimeChange = (text: string) => {
    // Remove any non-numeric characters
    const numericValue = text.replace(/[^0-9]/g, '');
    setStopTimeThreshold(numericValue);
  };

  const handleDistanceChange = (text: string) => {
    // Remove any non-numeric characters
    const numericValue = text.replace(/[^0-9]/g, '');
    setDistanceThreshold(numericValue);
  };

  if (isLoading) {
    return (
      <Surface style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ ...theme.fonts.bodyLarge }}>{t('loading')}</Text>
      </Surface>
    );
  }

  return (
    <Surface style={{ flex: 1 }}>
      <Appbar.Header style={{ backgroundColor: theme.colors.surfaceVariant }}>
        <Appbar.BackAction onPress={() => router.back()} />
        <Appbar.Content title={t('settings')} />
      </Appbar.Header>
      <ScrollView style={{ padding: 16 }}>
        <Surface elevation={3} style={styles.section}>
          <Text variant="titleMedium" style={styles.sectionTitle}>{t('notificationSettings')}</Text>
          <Surface elevation={0} style={styles.switchContainer}>
            <Text>{t('receiveNotifications')}</Text>
            <Switch
              value={notificationEnabled}
              onValueChange={setNotificationEnabled}
              style={{ marginLeft: 16 }}
            />
          </Surface>
          <TextInput
            label={t('stopTimeThreshold')}
            value={stopTimeThreshold}
            onChangeText={handleStopTimeChange}
            keyboardType="numeric"
            style={styles.input}
            right={<TextInput.Affix text="min" />}
            error={!!fieldErrors.stopTimeThreshold}
          />
          {fieldErrors.stopTimeThreshold && (
            <Text style={{ color: theme.colors.error, fontSize: 12, marginLeft: 4 }}>
              {fieldErrors.stopTimeThreshold}
            </Text>
          )}
          <TextInput
            label={t('distanceThreshold')}
            value={distanceThreshold}
            onChangeText={handleDistanceChange}
            keyboardType="numeric"
            style={styles.input}
            right={<TextInput.Affix text="m" />}
            error={!!fieldErrors.distanceThreshold}
          />
          {fieldErrors.distanceThreshold && (
            <Text style={{ color: theme.colors.error, fontSize: 12, marginLeft: 4 }}>
              {fieldErrors.distanceThreshold}
            </Text>
          )}
        </Surface>

        <Surface elevation={3} style={styles.section} testID="settings-driving-limits">
          <Text variant="titleMedium" style={styles.sectionTitle}>{t('workTime.mode.sectionTitle')}</Text>
          <DrivingLimitModeSetting
            mode={drivingLimitMode}
            onChange={changeDrivingLimitMode}
            disabled={savingDrivingLimitMode}
          />
        </Surface>

        <Surface elevation={3} style={styles.section}>
          <Text variant="titleMedium" style={styles.sectionTitle}>{t('permissions')}</Text>
          <Text variant="titleSmall" style={styles.sectionDescription}>{t('locationExplanation')}</Text>
          
          <List.Section>
            {Platform.OS !== 'web' && (
              <List.Item
                title={t('notificationsTitle', 'Notificações')}
                description={t('notificationsDescription', 'Para receber alertas e informações importantes')}
                left={props => <MaterialIcons {...props} name="notifications" size={24} color={theme.colors.primary} />}
                right={props => (
                  <Switch
                    value={permissions.notifications}
                    onValueChange={() => requestPermission('notifications')}
                    color={theme.colors.primary}
                  />
                )}
              />
            )}
            
            <List.Item
              title={t('foregroundLocation')}
              description={t('foregroundLocationDescription', 'Para mostrar sua localização atual no mapa')}
              left={props => <MaterialIcons {...props} name="location-on" size={24} color={theme.colors.primary} />}
              right={props => (
                <Switch
                  value={permissions.foregroundLocation}
                  onValueChange={() => requestPermission('foreground')}
                  color={theme.colors.primary}
                />
              )}
            />
            
            {Platform.OS !== 'web' && (
              <List.Item
                title={t('backgroundLocation')}
                description={t('backgroundLocationDescription', 'Para continuar rastreando mesmo quando o app estiver fechado')}
                left={props => <MaterialIcons {...props} name="location-searching" size={24} color={theme.colors.primary} />}
                right={props => (
                  <Switch
                    value={permissions.backgroundLocation}
                    onValueChange={() => requestPermission('background')}
                    color={theme.colors.primary}
                    disabled={!permissions.foregroundLocation}
                  />
                )}
              />
            )}
          </List.Section>
        </Surface>

        <Surface elevation={3} style={styles.section}>
          <Text variant="titleMedium" style={styles.sectionTitle}>{t('privacy.accountSection')}</Text>
          <List.Item
            title={t('privacy.settingsItemTitle')}
            description={t('privacy.settingsItemDesc')}
            left={props => <MaterialIcons {...props} name="privacy-tip" size={24} color={theme.colors.primary} />}
            onPress={() => router.push('/dashboard/privacy')}
          />
        </Surface>

        <Button mode="contained" onPress={savePreferences} style={styles.saveButton}>
          {t('save')}
        </Button>
      </ScrollView>

      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        action={{
          label: t('ok'),
          onPress: () => setSnackbarVisible(false),
        }}
        wrapperStyle={isLargeScreen ? { left: 0, right: 0, bottom: 120, alignItems: 'center' } : undefined}
        style={isLargeScreen ? { maxWidth: 560, borderRadius: 8 } : undefined}
      >
        {snackbarMessage}
      </Snackbar>

      <Portal>
        <Dialog visible={showDialog} onDismiss={() => setShowDialog(false)} style={Platform.OS === 'web' ? {
          maxWidth: 500,
          width: '90%',
          alignSelf: 'center',
        } : {}}>
          <Dialog.Title>{dialogTitle}</Dialog.Title>
          <Dialog.Content>
            <Text>{dialogMessage}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowDialog(false)}>OK</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </Surface>
  );
}

const styles = StyleSheet.create({
  section: {
    marginBottom: 32,
    padding: 16,
    borderRadius: 16,
  },
  sectionTitle: {
    marginBottom: 16,
  },
  sectionDescription: {
    marginBottom: 16,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  input: {
    marginTop: 8,
  },
  saveButton: {
    marginTop: 16,
    marginBottom: 32,
  },
});